import { useEffect, useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "@/auth/AuthProvider";
import type { AccountDocument } from "@/lib/brokerAccounts";
import {
  capacityProjectLabel,
  listCapacityProjects,
  submitCapacityRequest,
  type CapacityProject,
} from "@/lib/capacityRequest";
import {
  PAY_APPLICATION_DOCUMENT_TYPE,
  payApplicationEmptyMessage,
  payApplicationInput,
  payApplicationProjects,
  payApplicationReasons,
} from "@/lib/payApplication";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { messageFor } from "@/lib/errors";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { AccountDocumentSlot } from "@/components/AccountDocumentSlot";
import { FieldMarker } from "@/components/FieldMarker";
import { NumericField } from "@/components/NumericField";
import { SelectField } from "@/components/SelectField";
import { TextField } from "@/components/TextField";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S404 — "Show project progress with a pay application": the wireframe's
// AIA G702/G703 dialog, opened from a performance and payment row of the
// account's aggregate bar (and from "Show progress", which passes no order).
//
// It files a `capacity_wip` capacity request on the existing route: the bond,
// the pay application uploaded to the ACCOUNT (the WIP route's served document
// type), and the G702 figures as plain numbers and an ISO date (D-10;
// PMS-S849). It computes nothing from those figures — not a
// percentage, not a release: PMS owns that rule and may serve `estimatedRelease`,
// which the caller words (`estimatedReleaseLine`).
//
// NOTHING HERE IS OPTIMISTIC (NN #7): the caller hears `onFiled` only from the
// POST's 2xx. A refusal (PMS's 422, one already open) is PMS's sentence inline,
// and every value entered stays.

/** What the caller confirms from, all of it from PMS's answer or the request
 *  PMS accepted. */
export interface FiledPayApplication {
  orderNumber: string;
  applicationNumber: number;
  /** PMS's estimate, or null when it served none. */
  estimatedRelease: number | null;
}

const FIELDS = [
  "bondSubmissionId",
  "documentIds",
  "applicationNumber",
  "periodTo",
  "contractSum",
  "completedToDate",
] as const;

const TITLE = "Show project progress with a pay application";

export function PayApplicationDialog({
  accountId,
  orderId,
  open,
  onClose,
  onFiled,
}: {
  accountId: string;
  /** The order to preselect (a P&P row's); null to let the broker choose. */
  orderId: string | null;
  open: boolean;
  onClose: () => void;
  onFiled: (filed: FiledPayApplication) => void;
}) {
  const titleId = useId();
  const theme = useTheme();
  // Full screen on a phone; a dialog from `sm:`.
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
      fullScreen={phone}
      scroll="paper"
    >
      <DialogTitle id={titleId}>{TITLE}</DialogTitle>
      {/* Mounted per opening, so a closed dialog keeps no draft (NN #9). */}
      {open && (
        <PayApplicationForm
          accountId={accountId}
          orderId={orderId}
          busy={busy}
          setBusy={setBusy}
          onClose={onClose}
          onFiled={onFiled}
        />
      )}
    </Dialog>
  );
}

function PayApplicationForm({
  accountId,
  orderId,
  busy,
  setBusy,
  onClose,
  onFiled,
}: {
  accountId: string;
  orderId: string | null;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onClose: () => void;
  onFiled: (filed: FiledPayApplication) => void;
}) {
  const { authedRequest } = useAuth();
  const {
    rules,
    labelFor,
    error: typesError,
  } = useDocumentTypes(authedRequest);
  const reasonsId = useId();
  const [projects, setProjects] = useState<CapacityProject[] | null>(null);
  // Why there is nothing to choose, from the same read (BRK-S404).
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bond, setBond] = useState("");
  const [documents, setDocuments] = useState<AccountDocument[]>([]);
  const [applicationNumber, setApplicationNumber] = useState<
    number | undefined
  >(undefined);
  const [periodTo, setPeriodTo] = useState("");
  const [contractSum, setContractSum] = useState<number | undefined>(undefined);
  const [completedToDate, setCompletedToDate] = useState<number | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});

  // The bonds are read fresh for each opening: what PMS says holds capacity
  // now, never a list carried over from the bar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await listCapacityProjects(authedRequest, accountId);
        const rows = payApplicationProjects(all);
        if (cancelled) return;
        setEmptyMessage(payApplicationEmptyMessage(all));
        setProjects(rows);
        if (orderId !== null && rows.some((p) => p.orderId === orderId)) {
          setBond(orderId);
        }
      } catch (err) {
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, orderId]);

  const clear = (field: string) => {
    setError(null);
    setInvalid((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const draft = {
    bondSubmissionId: bond,
    documents,
    applicationNumber,
    periodTo,
    contractSum,
    completedToDate,
  };
  const reasons = payApplicationReasons(draft);
  const none = projects !== null && projects.length === 0;
  const showReasons =
    projects !== null && projects.length > 0 && reasons.length > 0;

  async function submit() {
    if (reasons.length > 0 || applicationNumber === undefined) return;
    const chosen = projects?.find((p) => p.orderId === bond);
    if (!chosen) return;
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      const filed = await submitCapacityRequest(
        authedRequest,
        accountId,
        payApplicationInput(draft),
      );
      onFiled({
        orderNumber: chosen.orderNumber,
        applicationNumber,
        estimatedRelease: filed?.estimatedRelease ?? null,
      });
    } catch (err) {
      const anchored = anchorServerError(err, FIELDS);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DialogContent>
        <div className="flex min-w-0 flex-col gap-4">
          <p className="text-sm text-brk-dim">
            Upload the latest AIA pay application (G702/G703) for a performance
            and payment bond. It shows how far the job has billed, so we can put
            capacity back into the program. An underwriter reviews every upload
            before capacity changes.
          </p>

          {loadError && (
            <p role="alert" className="text-sm text-brk-danger">
              {loadError}
            </p>
          )}
          {projects === null && !loadError && (
            <p role="status" className="text-sm text-brk-dim">
              Loading…
            </p>
          )}
          {none && emptyMessage && (
            <p className="text-sm text-brk-text">{emptyMessage}</p>
          )}

          {projects !== null && projects.length > 0 && (
            <>
              <SelectField
                label="Bond"
                placeholder="Select…"
                required
                value={bond}
                error={invalid.bondSubmissionId}
                onChange={(e) => {
                  setBond(e.target.value);
                  clear("bondSubmissionId");
                }}
                options={projects.map((p) => ({
                  value: p.orderId,
                  label: capacityProjectLabel(p),
                }))}
              />

              <div className="flex min-w-0 flex-col gap-2">
                <span className="flex items-baseline gap-1.5">
                  <span className="text-sm font-medium text-brk-text">
                    Pay application
                  </span>
                  <FieldMarker required />
                </span>
                <p className="text-xs text-brk-dim">
                  Include the G702 and G703 pages.
                </p>
                {typesError && (
                  <p role="alert" className="text-sm text-brk-danger">
                    {typesError}
                  </p>
                )}
                {rules ? (
                  <AccountDocumentSlot
                    accountId={accountId}
                    inputLabel="Upload the pay application"
                    types={[
                      {
                        key: PAY_APPLICATION_DOCUMENT_TYPE,
                        label: labelFor(PAY_APPLICATION_DOCUMENT_TYPE),
                      },
                    ]}
                    rules={rules}
                    documents={documents}
                    canChange={!busy}
                    onUploaded={(doc) => {
                      setDocuments((prev) => [...prev, doc]);
                      clear("documentIds");
                    }}
                    onRemoved={(removed) =>
                      setDocuments((prev) =>
                        prev.filter((d) => d.id !== removed),
                      )
                    }
                  />
                ) : (
                  !typesError && (
                    <p role="status" className="text-sm text-brk-dim">
                      Loading…
                    </p>
                  )
                )}
                {invalid.documentIds && (
                  <p role="alert" className="text-sm text-brk-danger">
                    {invalid.documentIds}
                  </p>
                )}
              </div>

              {/* Two across from `sm:`; stacked on a phone. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumericField
                  kind="number"
                  label="Application number"
                  required
                  min={1}
                  placeholder="e.g. 4"
                  value={applicationNumber}
                  error={invalid.applicationNumber}
                  onChange={(value) => {
                    setApplicationNumber(value);
                    clear("applicationNumber");
                  }}
                />
                {/* A date picker (D-10): the browser shows m/d/yyyy, the
                    value — and the wire — stay ISO YYYY-MM-DD. */}
                <TextField
                  label="Period to"
                  type="date"
                  required
                  value={periodTo}
                  error={invalid.periodTo}
                  onChange={(e) => {
                    setPeriodTo(e.target.value);
                    clear("periodTo");
                  }}
                />
                <NumericField
                  kind="currency"
                  label="Contract sum to date"
                  required
                  placeholder="Line 3 on the G702"
                  value={contractSum}
                  error={invalid.contractSum}
                  onChange={(value) => {
                    setContractSum(value);
                    clear("contractSum");
                  }}
                />
                <NumericField
                  kind="currency"
                  label="Completed and stored to date"
                  required
                  placeholder="Line 4 on the G702"
                  value={completedToDate}
                  error={invalid.completedToDate}
                  onChange={(value) => {
                    setCompletedToDate(value);
                    clear("completedToDate");
                  }}
                />
              </div>
            </>
          )}

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          {showReasons && (
            <ul
              id={reasonsId}
              className="flex flex-col gap-1 text-sm text-brk-dim"
            >
              {reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {/* Stacked at 375 (Submit on top), in a row from `sm:`. */}
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={buttonClass({ variant: "secondary" })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || none || projects === null || reasons.length > 0}
            aria-describedby={showReasons ? reasonsId : undefined}
            className={`${buttonClass()} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </DialogActions>
    </>
  );
}
